import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import {
  CheckCircle2, ShieldCheck, MapPin, ArrowRight, Heart, DollarSign,
  Users, Phone, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PublicNav from "@/components/PublicNav";
import Seo from "@/components/Seo";
import FaqSection, { faqJsonLd } from "@/components/FaqSection";
import PublicFooter from "@/components/PublicFooter";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/attribution";
import { toast } from "sonner";
import { getCareData, formatMoney } from "@/data/careTypes";

export default function Results() {
  const { leadId } = useParams();
  const location = useLocation();
  const [careType, setCareType] = useState(location.state?.care_type || null);
  const [explanation] = useState(location.state?.explanation || null);
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOpt, setSmsOpt] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    trackEvent("results_viewed", { lead_id: leadId });
  }, [leadId]);

  const submitSecondary = async (e) => {
    e.preventDefault();
    if (!zipCode.trim()) {
      toast.error("Please enter your zip code.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads/secondary", {
        lead_id: leadId,
        zip_code: zipCode,
        phone: phone || null,
        opted_in_sms: smsOpt,
      });
      trackEvent("secondary_submitted", { lead_id: leadId });
      setSubmitted(true);
      toast.success("All set — a specialist will reach out shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const displayCare = careType || "Assisted Living";
  const care = getCareData(displayCare);

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Seo
        title={`Your care match: ${displayCare}`}
        description={`Your personalized senior care recommendation from CareHomeConnect.`}
        path="/results"
        noindex
        jsonLd={faqJsonLd(care.faq)}
      />
      <PublicNav />

      {/* Result hero */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 pt-10 md:pt-16">
        <div className="text-center animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#9CAF88]/15 text-[#2C4C3B] text-xs font-medium mb-6">
            <CheckCircle2 className="w-3.5 h-3.5" /> Your personalized recommendation
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold text-[#1A2E24] tracking-tight leading-[1.05]">
            Based on your answers, your best fit is
          </h1>

          <div className="mt-8 card-soft p-8 md:p-10">
            <div className="text-5xl mb-4">{care.icon}</div>
            <div className="font-display text-3xl md:text-4xl font-semibold text-[#2C4C3B] mb-2" data-testid="result-care-type">
              {displayCare}
            </div>
            <p className="text-[#9CAF88] font-medium text-sm uppercase tracking-wider mb-5">
              {care.tagline}
            </p>
            <p className="text-[#1A2E24] leading-relaxed text-base md:text-lg max-w-xl mx-auto">
              {explanation || care.longDescription}
            </p>
          </div>
        </div>
      </section>

      {/* What's included + Who it's best for */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 mt-16 grid md:grid-cols-2 gap-5">
        <div className="card-soft p-6 md:p-8" data-testid="care-includes">
          <div className="w-10 h-10 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-5 h-5 text-[#2C4C3B]" />
          </div>
          <h2 className="font-display text-xl md:text-2xl font-semibold text-[#1A2E24] mb-4">What's included</h2>
          <ul className="space-y-2.5">
            {care.includes.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[#1A2E24]">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#9CAF88] shrink-0" />
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="card-soft p-6 md:p-8" data-testid="care-bestfor">
          <div className="w-10 h-10 rounded-xl bg-[#E07A5F]/15 flex items-center justify-center mb-4">
            <Users className="w-5 h-5 text-[#C96B52]" />
          </div>
          <h2 className="font-display text-xl md:text-2xl font-semibold text-[#1A2E24] mb-4">Who it's best for</h2>
          <ul className="space-y-2.5">
            {care.bestFor.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[#1A2E24]">
                <Heart className="w-3.5 h-3.5 text-[#E07A5F] mt-1 shrink-0" />
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Cost band */}
      <section className="max-w-5xl mx-auto px-6 md:px-8 mt-5">
        <div className="card-soft p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5">
          <div className="w-12 h-12 rounded-xl bg-[#9CAF88]/15 flex items-center justify-center shrink-0">
            <DollarSign className="w-6 h-6 text-[#2C4C3B]" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-[#9CAF88] font-semibold mb-1">
              Typical 2026 monthly cost
            </div>
            <div className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24]">
              {formatMoney(care.costLow)} – {formatMoney(care.costHigh)}
              <span className="ml-2 text-sm font-normal text-[#5C6B62]">/month, US average</span>
            </div>
            <p className="text-sm text-[#5C6B62] leading-relaxed mt-2">
              Costs vary by city and care level. Funding sources include private savings, long-term care insurance, VA Aid &amp; Attendance, and (sometimes) Medicaid waivers. Your specialist will build a personalized funding plan with you.
            </p>
          </div>
        </div>
      </section>

      {/* Differs from */}
      {care.differsFrom && (
        <section className="max-w-5xl mx-auto px-6 md:px-8 mt-5">
          <div className="rounded-3xl bg-white border border-[#E2DFD8] p-6 md:p-8" data-testid="care-differs">
            <div className="text-xs uppercase tracking-wider text-[#9CAF88] font-semibold mb-2">
              How it's different
            </div>
            <h2 className="font-display text-xl md:text-2xl font-semibold text-[#1A2E24] mb-3">
              {displayCare} vs. other options
            </h2>
            <p className="text-[#1A2E24] leading-relaxed">{care.differsFrom}</p>
          </div>
        </section>
      )}

      {/* Placement Director CTA — the conversion moment */}
      {!submitted ? (
        <section className="max-w-5xl mx-auto px-6 md:px-8 mt-12">
          <div className="bg-gradient-to-br from-[#2C4C3B] to-[#1A2E24] rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#9CAF88]/15 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="relative grid md:grid-cols-5 gap-8 items-start">
              <div className="md:col-span-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-medium mb-5">
                  <Sparkles className="w-3.5 h-3.5" /> The next step that matters
                </div>
                <h2 className="font-display text-2xl md:text-3xl font-semibold leading-tight">
                  Don&apos;t do this alone. Talk to a Placement Director.
                </h2>
                <p className="mt-4 text-[#F7F5F0]/85 text-sm md:text-base leading-relaxed">
                  Your recommendation is the right starting point — but the <em>specific</em> community that fits your loved one&apos;s personality, budget, and zip code? That takes a human.
                </p>
                <div className="mt-6 space-y-3 text-sm">
                  {[
                    { icon: Heart, t: "100% free for families — always" },
                    { icon: ShieldCheck, t: "Vetted, licensed local advisors" },
                    { icon: Users, t: "Just 1 specialist (not 5+ like other sites)" },
                    { icon: Phone, t: "Most reach out within 24 hours" },
                  ].map((x, i) => (
                    <div key={i} className="flex items-center gap-3 text-[#F7F5F0]/90">
                      <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                        <x.icon className="w-3.5 h-3.5" />
                      </div>
                      {x.t}
                    </div>
                  ))}
                </div>
              </div>

              <form onSubmit={submitSecondary} className="md:col-span-3 bg-white rounded-2xl p-6 md:p-8 space-y-5 text-[#1A2E24]" data-testid="secondary-form">
                <div className="font-display text-lg font-semibold text-[#1A2E24]">
                  Connect me with a Placement Director
                </div>
                <div>
                  <Label htmlFor="zip" className="text-[#2C4C3B] font-medium flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Zip code (where care is needed)
                  </Label>
                  <Input
                    id="zip"
                    data-testid="secondary-zip"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                    placeholder="10001"
                    required
                  />
                  <p className="mt-1.5 text-xs text-[#5C6B62]">We&apos;ll match you with a specialist who knows this market.</p>
                </div>

                <div>
                  <Label htmlFor="phone2" className="text-[#2C4C3B] font-medium">
                    Phone <span className="text-xs text-[#5C6B62] font-normal">(optional — fastest way to connect)</span>
                  </Label>
                  <Input
                    id="phone2"
                    type="tel"
                    data-testid="secondary-phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1.5 h-12 rounded-xl bg-[#F7F5F0] border-[#E2DFD8]"
                    placeholder="(555) 123-4567"
                  />
                </div>

                {phone && (
                  <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-[#9CAF88]/10 border border-[#9CAF88]/20">
                    <Checkbox
                      data-testid="secondary-sms-opt"
                      checked={smsOpt}
                      onCheckedChange={(v) => setSmsOpt(!!v)}
                      className="mt-0.5 border-[#2C4C3B] data-[state=checked]:bg-[#2C4C3B]"
                    />
                    <span className="text-sm text-[#2C4C3B]">
                      Also text me my results summary &amp; the specialist&apos;s intro.
                    </span>
                  </label>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E07A5F] hover:bg-[#C96B52] text-white rounded-xl h-12 font-medium"
                  data-testid="secondary-submit"
                >
                  {loading ? "Sending…" : "Connect me with a Placement Director"}
                  {!loading && <ArrowRight className="ml-2 w-4 h-4" />}
                </Button>

                <div className="flex items-center gap-1.5 text-xs text-[#5C6B62] justify-center pt-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#9CAF88]" />
                  Private. No cost to families. Unsubscribe anytime.
                </div>
              </form>
            </div>
          </div>
        </section>
      ) : (
        <section className="max-w-3xl mx-auto px-6 md:px-8 mt-12 text-center" data-testid="submitted-state">
          <div className="card-soft p-10">
            <div className="inline-flex w-14 h-14 rounded-full bg-[#9CAF88]/20 items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-[#2C4C3B]" />
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-semibold text-[#1A2E24]">You&apos;re all set</h2>
            <p className="mt-3 text-[#5C6B62]">
              Your results are on their way to your inbox. A vetted Placement Director who knows your area will reach out — often within 24 hours.
            </p>
            <p className="mt-2 text-sm text-[#5C6B62]">In the meantime, read what other families experienced.</p>
            <Link to="/articles">
              <Button className="mt-6 bg-[#2C4C3B] hover:bg-[#1A2E24] text-white rounded-xl h-12 px-8 font-medium">
                Explore more stories <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      )}

      {/* Care-type-specific FAQ */}
      <FaqSection
        eyebrow={`${displayCare} questions`}
        title={`What families ask us about ${displayCare}`}
        description="The honest answers we wish more places gave."
        items={care.faq}
        testIdPrefix="results-faq"
      />

      <PublicFooter />
    </div>
  );
}
